import { StoredTextureLike } from '@utils/sceneObjects/linked/textureCatalogApi'
import {
  alignTextureZoneNamesWithZones,
  findZoneKeyForTexture,
  sortTexturesForZoneSync,
} from '@utils/sceneObjects/linked/linkedZoneTextureMapping'
import {
  isPuertaInheritanceLinkedCatalog,
  isStaleZoneCatalogImg,
  replaceZoneMaterialFromTexture,
} from '@utils/sceneObjects/linked/linkedTextureFields'

type LinkedTextureLike = StoredTextureLike & {
  idAcabadoUnidadModeloAcabado?: string
  complemento?: string
  description?: string
  descripcion?: string
  img?: string
}

export type SyncZonesLogContext = {
  itemId?: string
  catalogID?: string
  source?: string
}

export { findZoneKeyForTexture } from '@utils/sceneObjects/linked/linkedZoneTextureMapping'

export function textureToZoneMaterial(
  texture: LinkedTextureLike,
  existingMaterial: Record<string, unknown> = {},
): Record<string, unknown> {
  return {
    ...existingMaterial,
    name: texture.name ?? existingMaterial.name,
    url: texture.url ?? existingMaterial.url,
    color: texture.color ?? existingMaterial.color,
    groupID: texture.groupID ?? existingMaterial.groupID,
    searchID: texture.searchID ?? existingMaterial.searchID,
    id: texture.id ?? existingMaterial.id,
    linkedTextureID: texture.linkedTextureID ?? existingMaterial.linkedTextureID,
    repeatX: texture.repeatX ?? existingMaterial.repeatX,
    repeatY: texture.repeatY ?? existingMaterial.repeatY,
    opacity: texture.opacity ?? existingMaterial.opacity,
    roughness: texture.roughness ?? existingMaterial.roughness,
    metalness: texture.metalness ?? existingMaterial.metalness,
    mirror: texture.mirror ?? existingMaterial.mirror,
    envMapIntensity: texture.envMapIntensity ?? existingMaterial.envMapIntensity,
    bumpMap: texture.bumpMap ?? existingMaterial.bumpMap,
    bumpScale: texture.bumpScale ?? existingMaterial.bumpScale,
    displacement: texture.displacement ?? existingMaterial.displacement,
    displacementScale: texture.displacementScale ?? existingMaterial.displacementScale,
    textureRotation: texture.textureRotation ?? existingMaterial.textureRotation,
    complemento: texture.complemento ?? existingMaterial.complemento,
    description:
      texture.description ?? texture.descripcion ?? texture.name ?? existingMaterial.description,
    img: texture.img ?? existingMaterial.img,
    acabado: texture.acabado ?? existingMaterial.acabado,
    modelo: texture.modelo ?? existingMaterial.modelo,
    zoneDisplayName: texture.zoneDisplayName ?? existingMaterial.zoneDisplayName,
    zone: texture.zone ?? texture.zoneDisplayName ?? existingMaterial.zone,
  }
}

const MATERIAL_COMPARE_FIELDS = [
  'name',
  'url',
  'envMapIntensity',
  'metalness',
  'roughness',
  'searchID',
  'id',
] as const

export function logLinkedItemMaterialSync(
  item: {
    textures?: LinkedTextureLike[]
    zones?: Record<string, unknown>
    catalogID?: string
  },
  context: SyncZonesLogContext,
): void {
  const mismatches: Array<Record<string, unknown>> = []

  for (const texture of item.textures ?? []) {
    const zoneKey = findZoneKeyForTexture(item.zones ?? {}, texture)
    if (!zoneKey) continue

    const mat = (item.zones?.[zoneKey] as { material?: Record<string, unknown> } | undefined)
      ?.material
    if (!mat) continue

    const diff: Record<string, { texture: unknown; zone: unknown }> = {}
    for (const field of MATERIAL_COMPARE_FIELDS) {
      const textureValue = texture[field]
      const zoneValue = mat[field]
      if (textureValue !== undefined && zoneValue !== undefined && textureValue !== zoneValue) {
        diff[field] = { texture: textureValue, zone: zoneValue }
      }
    }

    if (Object.keys(diff).length > 0) {
      mismatches.push({
        zoneKey,
        zoneDisplayName: texture.zoneDisplayName,
        textureName: texture.name,
        diff,
      })
    }
  }

  if (mismatches.length > 0) {
    // console.warn('[syncLinkedItemZonesFromTextures] desajuste textures ↔ zones', {
      // ...context,
      // catalogID: item.catalogID ?? context.catalogID,
      // mismatches,
    // })
  }
}

/** Copia propiedades de item.textures a item.zones[*].material (render 3D). */
export function syncLinkedItemZonesFromTextures(
  item: {
    textures?: LinkedTextureLike[]
    zones?: Record<string, unknown>
    catalogID?: string
  },
  context?: SyncZonesLogContext,
): number {
  if (!item.textures?.length || !item.zones) return 0

  item.textures = alignTextureZoneNamesWithZones(item.textures, item.zones)

  const assignedZoneKeys = new Set<string>()
  let synced = 0

  // Zonas de mesh agrupadas por nombre lógico (Puerta, Armazon…). Permite mapear
  // posicionalmente las N texturas de una zona lógica a sus N meshes (p. ej. muebles
  // de 2 puertas), aunque el zoneName de la textura esté obsoleto o no sea único.
  const zoneKeysByDisplayName = new Map<string, string[]>()
  for (const [key, rawZone] of Object.entries(item.zones)) {
    const mat = (rawZone as { material?: Record<string, unknown> })?.material
    if (!mat) continue
    const displayName = (mat.zoneDisplayName ?? mat.zone) as string | undefined
    if (!displayName) continue
    const bucket = zoneKeysByDisplayName.get(displayName)
    if (bucket) bucket.push(key)
    else zoneKeysByDisplayName.set(displayName, [key])
  }

  const takeNextUnassignedZoneKey = (
    texture: LinkedTextureLike,
  ): string | null => {
    const candidates = [texture.zoneDisplayName, texture.zone].filter(Boolean) as string[]
    for (const displayName of candidates) {
      const bucket = zoneKeysByDisplayName.get(displayName)
      if (!bucket) continue
      const next = bucket.find((key) => !assignedZoneKeys.has(key))
      if (next) return next
    }
    return null
  }

  for (const texture of sortTexturesForZoneSync(item.textures, item.zones as Record<string, { name?: string; material?: Record<string, unknown> }>)) {
    let zoneKey: string | null = null

    if (texture.zoneName && item.zones[texture.zoneName]) {
      zoneKey = texture.zoneName
    } else {
      zoneKey = findZoneKeyForTexture(item.zones, texture)
    }

    // Si la clave no resuelve o ya fue asignada (varios meshes con el mismo nombre
    // lógico), tomar la siguiente zona libre de ese displayName.
    if (!zoneKey || !item.zones[zoneKey] || assignedZoneKeys.has(zoneKey)) {
      const fallbackKey = takeNextUnassignedZoneKey(texture)
      if (fallbackKey) {
        zoneKey = fallbackKey
      }
    }

    if (!zoneKey || !item.zones[zoneKey]) continue

    const zone = item.zones[zoneKey] as { material?: Record<string, unknown> }
    if (!zone.material) continue

    if (assignedZoneKeys.has(zoneKey)) {
      // console.warn('[syncLinkedItemZonesFromTextures] zona ya asignada, se omite textura duplicada', {
        // zoneKey,
        // textureZoneName: texture.zoneName,
        // zoneDisplayName: texture.zoneDisplayName,
        // textureName: texture.name,
        // source: context?.source,
      // })
      continue
    }

    zone.material = isPuertaInheritanceLinkedCatalog(item)
      ? replaceZoneMaterialFromTexture(texture, zone.material)
      : textureToZoneMaterial(texture, zone.material)

    const mat = zone.material as Record<string, unknown>
    if (
      isPuertaInheritanceLinkedCatalog(item) &&
      isStaleZoneCatalogImg(String(texture.name ?? ''), mat.img)
    ) {
      delete mat.img
      mat.description = texture.name ?? mat.description
      mat.descripcion = texture.name ?? mat.descripcion
    }

    assignedZoneKeys.add(zoneKey)

    if (texture.idAcabadoUnidadModeloAcabado) {
      ;(item.zones[zoneKey] as { idAcabadoUnidadModeloAcabado?: string }).idAcabadoUnidadModeloAcabado =
        texture.idAcabadoUnidadModeloAcabado
    }

    synced++
  }

  if (context) {
    logLinkedItemMaterialSync(item, { ...context, source: `${context.source ?? 'sync'}:after` })
  }

  return synced
}
